import { useCallback } from 'react';
import { useAtomCallback } from 'jotai/utils';
import { useNavigate } from 'react-router';
import { useLoadingBarState } from '../../../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import { ROUTES } from '../../../../../constants/RouteNameMapping';
import {
  createTemplateAssignment,
  deleteTemplateAssignment,
  updateTemplateAssignment,
} from '../../../../../services/queries/KPITemplatesQueries';
import { getErrorMessage } from '../../../../../services/queryHelpers';
import { kpiTemplateAssignmentsListStateJ } from '../../../../../services/state/KPI/KPICompaniesStateJ';
import { LoadingId } from '../../../../../types';
import { formatStringArray } from '../../../../../util/formatters/StringFormatters';

import { useInvalidateKPIRequests } from '../../KPIRequests/KPIRequestResponse/hooks/useInvalidateKPIRequests';
import { TemplateAssignment } from '../../../../../schemas/template-assignment-schema';

export type CreateAssignRequestPayload = {
  id?: number;
  data: Partial<TemplateAssignment>;
  templateName: string;
  companyName: string;
};

const ROUTE_TO_SEND_TAB = `/${ROUTES.KPI}/${ROUTES.KPI_REQUESTS}`;

/**@deprecated: use useTemplateAssignmentActions */
export function useKPITemplateAssignments() {
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const invalidateKpiRequests = useInvalidateKPIRequests();
  const navigate = useNavigate();

  const createTemplateAssignmentRequest = useAtomCallback(
    useCallback(
      async (get, set, payload: Partial<CreateAssignRequestPayload>) => {
        const { data, templateName, companyName } = payload as CreateAssignRequestPayload;
        actions.startLoading(LoadingId.createTemplateAssignmentRequest);

        try {
          const response = await createTemplateAssignment(data);
          await invalidateKpiRequests();

          const current = (await get(kpiTemplateAssignmentsListStateJ)) ?? [];
          set(kpiTemplateAssignmentsListStateJ, [...current, response]);

          const successMsg = `Request "${templateName}" has been successfully assigned to Company "${companyName}" with respondent ${formatStringArray(
            (data.respondent as string[]) ?? []
          )}`;

          pushSuccessToast({
            message: successMsg,
            buttonLabel: 'Go to request',
            buttonAction: () => {
              navigate(ROUTE_TO_SEND_TAB);
            },
          });

          return response;
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, `Failed to assign ${templateName} to ${companyName}.`);
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.createTemplateAssignmentRequest);
        }
      },
      [actions, invalidateKpiRequests, navigate, pushErrorToast, pushSuccessToast]
    )
  );

  const updateTemplateAssignmentRequest = useAtomCallback(
    useCallback(
      async (get, set, payload: Partial<CreateAssignRequestPayload>) => {
        const { data, templateName, companyName } = payload as CreateAssignRequestPayload;
        actions.startLoading(LoadingId.updateTemplateAssignmentRequest);

        try {
          const response = await updateTemplateAssignment(data);
          await invalidateKpiRequests();
          const current = (await get(kpiTemplateAssignmentsListStateJ)) ?? [];
          const newValues = current.map((value) => (value.id === response.id ? response : value));
          set(kpiTemplateAssignmentsListStateJ, [...newValues]);

          const successMsg = `Request "${templateName}" has been successfully updated to Company "${companyName}"`;

          pushSuccessToast({
            message: successMsg,
            buttonLabel: 'Go to request',
            buttonAction: () => {
              navigate(ROUTE_TO_SEND_TAB);
            },
          });

          return response;
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, `Failed to assign ${templateName} to ${companyName}.`);
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.updateTemplateAssignmentRequest);
        }
      },
      [actions, invalidateKpiRequests, navigate, pushErrorToast, pushSuccessToast]
    )
  );

  const deleteTemplateAssignmentRequest = useAtomCallback(
    useCallback(
      async (get, set, payload: Partial<TemplateAssignment>) => {
        const { templateName = '', companyName = '' } = payload as CreateAssignRequestPayload;

        actions.startLoading(LoadingId.deleteTemplateAssignmentRequest);

        try {
          const response = await deleteTemplateAssignment(payload.id as number);
          await invalidateKpiRequests();

          const current = (await get(kpiTemplateAssignmentsListStateJ)) ?? [];
          set(
            kpiTemplateAssignmentsListStateJ,
            current.filter((assignment) => assignment.id !== payload.id)
          );

          const successMsg = `Template "${templateName}" was removed from "${companyName}" successfully`;

          pushSuccessToast({
            message: successMsg,
          });

          return response;
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(
            err,
            `Failed delete assignation of ${templateName} from ${companyName}.`
          );
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.deleteTemplateAssignmentRequest);
        }
      },
      [actions, invalidateKpiRequests, pushErrorToast, pushSuccessToast]
    )
  );

  return {
    deleteTemplateAssignmentRequest,
    updateTemplateAssignmentRequest,
    createTemplateAssignmentRequest,
  };
}
